import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';

import { InstitutionDataService } from '@cpd-app/core-module/services';
import { Institution } from '@cpd-app/core-module/models';

import { AppState } from '..';
import { occupationDetailWithBusinessRulesReady } from '../occupation-detail/occupation-detail.actions';
import * as OccupationDetailSelectors from '../occupation-detail/occupation-detail.selectors';
import * as InstitutionActions from './institution.actions';
import * as InstitutionSelectors from './institution.selectors';

@Injectable()
export class InstitutionEffects {
  occupationDetailWithBusinessRulesReady$ = createEffect(() =>
    this.actions$.pipe(
      ofType(occupationDetailWithBusinessRulesReady),
      map(() => {
        return InstitutionActions.loadInstitutions();
      })
    )
  );

  /**
   * The school list, for every action that changes what it should hold.
   *
   * OCC.5 Part 3A. These were three effects (load, filter change, page), each
   * with its own switchMap, so a request from one never cancelled a request
   * from another and whichever came back LAST won. That was always a risk (a
   * search typed while the first page loads), and step 2 made it a real one:
   * the page's first load goes out occupation-wide, then the program goal is
   * applied, and if the occupation-wide answer landed second the member saw
   * 2,483 schools under a heading naming a credential 15 of them offer. One
   * stream, one switchMap: the latest options always win.
   *
   * Every request still reads the options from the store, so paging keeps
   * every filter and a filter change keeps the window rules in the reducer.
   *
   * Guarded on the occupation being loaded. The goal can be applied before the
   * occupation detail reaches the store, and destructuring a null occupation
   * threw inside the effect; the business-rules-ready load follows anyway.
   */
  loadInstitutions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        InstitutionActions.loadInstitutions,
        InstitutionActions.updateInstitutionsForOccupationOptions,
        InstitutionActions.setInstitutionsPage
      ),
      withLatestFrom(
        this.store.pipe(
          select(OccupationDetailSelectors.selectOccupationDetail)
        ),
        this.store.pipe(
          select(InstitutionSelectors.selectInstitutionsForOccupationOptions)
        )
      ),
      filter(([, occupation]) => !!occupation?.onetCode),
      switchMap(([, { onetCode }, options]) => {
        return this.institutionDataService
          .getInstitutionsForOccupation(onetCode, options)
          .pipe(
            map(({ items, totalCount }) =>
              InstitutionActions.institutionsLoadedSuccess({
                institutions: items,
                totalCount
              })
            ),
            catchError((error) =>
              of(InstitutionActions.institutionsLoadedFailure({ error }))
            )
          );
      })
    );
  });

  setSelectedInstitution$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstitutionActions.setSelectedInstitution),
      switchMap(({ institutionId }) => {
        return this.institutionDataService.getSchoolDetails(institutionId).pipe(
          map((institutionDetails) =>
            InstitutionActions.institutionDetailsLoadedSuccess({
              institutionDetails
            })
          ),
          // A details failure is not a list failure. The dedicated action was
          // already declared and this effect dispatched the list one, so the
          // panel could not tell that it was the one that had failed.
          catchError((error) =>
            of(InstitutionActions.institutionDetailsLoadedFailure({ error }))
          )
        );
      })
    );
  });

  loadInstitutionsByProgram$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(InstitutionActions.loadInstitutionsByProgram),
      switchMap(({ input }) =>
        this.institutionDataService.getInstitutionsByProgram(input).pipe(
          map((institutions) =>
            InstitutionActions.loadInstitutionsByProgramSuccess({
              institutions
            })
          ),
          catchError((error) =>
            of(
              InstitutionActions.loadInstitutionsByProgramFailure({
                error
              })
            )
          )
        )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private institutionDataService: InstitutionDataService
  ) {}
}
